import { createApiHooks, useGenericGet, useGenericPost } from '../../../../../hooks/use-api.hook';
import { apiClient } from '../../../../../services/api-client.service';
import type {
  Role,
  RoleCreate,
  RoleGetFiltersResponse,
  RoleGetResponse,
  RoleUpdate,
} from '../types/role.types';
import type { UseListParams } from '../../../../../types/pagination.types';
import { useMemo } from 'react';
import { useActiveTenant } from '../../../../../states/active-tenant.atom';

const useRoleBasePath = () => {
  const { activeTenant } = useActiveTenant();
  return useMemo(() => {
    return `/v1/admin/tenants/${activeTenant?.id}/roles`;
  }, [activeTenant]);
};

export const useRoleHooks = () => {
  const roleBasePath = useRoleBasePath();
  const { activeTenant } = useActiveTenant();

  return createApiHooks<Role, RoleGetResponse, RoleCreate, RoleUpdate>({
    basePath: roleBasePath,
    queryKey: ['Roles', 'Tenant', activeTenant!.id],
    apiClient,
  });
};

export const useRoleGetFilters = (queryOptions?: any) => {
  const roleBasePath = useRoleBasePath();
  return useGenericGet<RoleGetFiltersResponse>(
    `${roleBasePath}/filters`,
    ['Roles', 'filters'],
    apiClient,
    queryOptions
  );
};

// Create a custom hook that returns all the role hooks
export const useRoleApi = () => {
  const roleHooks = useRoleHooks();

  return {
    useGet: roleHooks.useGet,
    useCreate: roleHooks.useCreate,
    useUpdate: roleHooks.useUpdate,
    useDelete: roleHooks.useDelete,
    useList: (params: UseListParams) => roleHooks.useList(params),
  };
};

// Export individual hooks for backward compatibility
export const useRoleGet = (id: string | number, queryOptions?: any) => {
  const { useGet } = useRoleApi();
  return useGet(id, queryOptions);
};

export const useRoleCreate = (mutationOptions?: any) => {
  const { useCreate } = useRoleApi();
  return useCreate(mutationOptions);
};

export const useRoleUpdate = (mutationOptions?: any) => {
  const { useUpdate } = useRoleApi();
  return useUpdate(mutationOptions);
};

export const useRoleDelete = (mutationOptions?: any) => {
  const { useDelete } = useRoleApi();
  return useDelete(mutationOptions);
};

export const useRoleList = (params: UseListParams) => {
  const { useList } = useRoleApi();
  return useList(params);
};
